import { useEffect, useState, useCallback } from 'react';
import { useToast } from '../ui/Toast';
import api from '../../services/api';

const DailyGoalWidget = () => {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [alerted, setAlerted] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const res = await api.get('/dashboard/daily-progress');
      if (res.data.success) {
        const d = res.data.data;
        setData(d);
        // Warn once if at max loss
        if (d.isAtMaxLoss && !alerted) {
          toast.warning(`Daily loss limit reached (-$${d.maxLoss}). Consider stopping for today.`);
          setAlerted(true);
        }
      }
    } catch (err) {
      // Widget is optional — fail silently
      console.error('DailyGoalWidget:', err.message);
    }
  }, [alerted, toast]);

  useEffect(() => { fetch(); }, [fetch]);

  if (!data) return null;

  const { todayPnl, goal, maxLoss, progressPercent, tradesToday } = data;
  const isPositive = todayPnl >= 0;
  const atMax = data.isAtMaxLoss;
  const pct = Math.min(Math.abs(progressPercent), 100);

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
          <span>🎯</span> Today&apos;s Goal
        </h3>
        <span className="text-xs text-slate-400">{tradesToday} trade{tradesToday !== 1 ? 's' : ''} today</span>
      </div>

      {/* P&L display */}
      <div className={`text-2xl font-bold mb-2 ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
        {isPositive ? '+' : ''}${Math.abs(todayPnl).toFixed(2)}
      </div>

      {/* Progress bar */}
      <div className="relative h-2 bg-slate-700 rounded-full overflow-hidden mb-2">
        <div
          className={`h-full rounded-full transition-all duration-500 ${atMax ? 'bg-red-500' : isPositive ? 'bg-emerald-500' : 'bg-red-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex justify-between text-xs text-slate-500">
        <span>{atMax ? '⚠️ Max loss hit' : isPositive ? `${progressPercent}% of goal` : `${Math.abs(progressPercent)}% toward max loss`}</span>
        <span>Goal: ${goal} | Max loss: -${maxLoss}</span>
      </div>
    </div>
  );
};

export default DailyGoalWidget;
